import { useEffect, useRef, useState } from "react";
import AddBlogForm from "./AddBlogForm";
import { Blog } from "@/types";
import { Button } from "./ui/button";
import { Pencil, Trash2 } from "lucide-react";
import { BlogRepository } from "@/infrastructure/repositories/BlogRepository";

const PAGE_SIZE = 10;

type FeedbackType = "success" | "error";

export function CreateBlogPage() {
  const blogRepo = new BlogRepository();

  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState<Blog | null>(null);
  const [blogToDelete, setBlogToDelete] = useState<Blog | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [feedback, setFeedback] = useState<{
    type: FeedbackType;
    message: string;
  } | null>(null);
  const initialLoadedRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const fetchedPagesRef = useRef<Set<number>>(new Set());

  const resolveUserId = (): number => {
    const candidates = [
      localStorage.getItem("current_user_swimmer_id"),
      localStorage.getItem("selected_swimmer_id"),
      localStorage.getItem("user_id"),
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    return 0;
  };

  const fetchBlogsForCategories = async (offset: number, threshold: number) => {
    const cat19 = await blogRepo.getBlogList(offset, threshold, "19");
    const cat20 = await blogRepo.getBlogList(offset, threshold, "20");
    const merged = [...cat19, ...cat20];
    const unique = Array.from(
      new Map(merged.map((b: any) => [String(b.post_id), b])).values()
    );

    return {
      blogs: unique,
      hasMore: cat19.length === threshold || cat20.length === threshold,
    };
  };

  const mergeUniqueBlogs = (current: Blog[], incoming: Blog[]) =>
    Array.from(new Map([...current, ...incoming].map((b) => [String(b.id), b])).values());

  const normalizeImageAlt = (apiBlog: any): string => {
    const raw =
      apiBlog.image_alt ??
      apiBlog.post_img_alt ??
      apiBlog.post_image_alt ??
      apiBlog.alt_text ??
      "";
    const text = String(raw).trim();
    if (!text || text.toLowerCase() === "null" || text.toLowerCase() === "undefined") {
      return "";
    }
    return text;
  };

  // ----------------------------------
  // BLOG MAPPER
  // ----------------------------------
  const mapApiBlogToBlog = (apiBlog: any): Blog => ({
    id: Number(apiBlog.post_id),
    title: apiBlog.post_title ?? "",
    slug: apiBlog.post_slug ?? "",
    excerpt: apiBlog.post_meta_desc ?? "",
    content: apiBlog.post_desc ?? "",
    image: apiBlog.post_img
      ? `${apiBlog.post_img}`
      : "",
    image_alt: normalizeImageAlt(apiBlog),
    category: String(apiBlog.post_category ?? ""),
    featured: String(apiBlog.post_category) === "19",
    readTime: apiBlog.read_time ? String(apiBlog.read_time) : "5 min read",
    author: apiBlog.author ?? "Admin",
    date: apiBlog.post_created_at
      ? new Date(Number(apiBlog.post_created_at) * 1000).toLocaleDateString()
      : "",
  });

  const handleEditBlog = async (blog: Blog) => {
    setSaving(true);
    try {
      const detail = await blogRepo.getBlogDetail(Number(blog.id));
      const detailItem = Array.isArray(detail) ? detail[0] : detail;
      if (detailItem) {
        setEditingBlog(mapApiBlogToBlog(detailItem));
      } else {
        setEditingBlog(blog);
      }
      setIsAddOpen(true);
    } catch {
      setEditingBlog(blog);
      setIsAddOpen(true);
    } finally {
      setSaving(false);
    }
  };

  // ----------------------------------
  // INITIAL LOAD
  // ----------------------------------
  useEffect(() => {
    const loadInitialBlogs = async () => {
      if (initialLoadedRef.current) return;
      initialLoadedRef.current = true;
      setLoading(true);

      const { blogs: apiBlogs, hasMore: nextHasMore } =
        await fetchBlogsForCategories(1, PAGE_SIZE);
      setBlogs(apiBlogs.map(mapApiBlogToBlog));
      fetchedPagesRef.current.add(1);
      setPage(2);
      setHasMore(nextHasMore);

      setLoading(false);
    };

    loadInitialBlogs();
  }, []);

  // ----------------------------------
  // LOAD MORE ON SCROLL
  // ----------------------------------
  const loadMoreBlogs = async () => {
    if (loadingMoreRef.current || loadingMore || !hasMore) return;
    if (fetchedPagesRef.current.has(page)) return;

    loadingMoreRef.current = true;
    setLoadingMore(true);

    const { blogs: apiBlogs, hasMore: nextHasMore } =
      await fetchBlogsForCategories(page, PAGE_SIZE);
    setBlogs(prev => mergeUniqueBlogs(prev, apiBlogs.map(mapApiBlogToBlog)));
    fetchedPagesRef.current.add(page);
    setPage(prev => prev + 1);
    setHasMore(nextHasMore);

    setLoadingMore(false);
    loadingMoreRef.current = false;
  };

  useEffect(() => {
    const onScroll = () => {
      if (
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 300
      ) {
        loadMoreBlogs();
      }
    };

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [page, hasMore, loadingMore]);

  // ----------------------------------
  // CONFIRM DELETE
  // ----------------------------------
  const confirmDelete = async () => {
    if (!blogToDelete) return;

    setSaving(true);

    const success = await blogRepo.deleteBlog(blogToDelete.id);

    if (success) {
      setBlogs(prev => prev.filter(b => b.id !== blogToDelete.id));
      setFeedback({
        type: "success",
        message: "Blog deleted successfully",
      });
    } else {
      setFeedback({
        type: "error",
        message: "Failed to delete blog",
      });
    }

    setBlogToDelete(null);
    setSaving(false);
  };

  // ----------------------------------
  // CREATE / UPDATE
  // ----------------------------------
 const handleSubmit = async (blog: Blog) => {
  setSaving(true);

  const userId = resolveUserId();
  if (userId <= 0) {
    setFeedback({
      type: "error",
      message: "Unable to upload image: user_id is missing. Please sign in again.",
    });
    setSaving(false);
    return;
  }

  let uploadedImageUrl = blog.image;
  if (blog.imageFile) {
    const uploadResult = await blogRepo.uploadBlogImage(userId, blog.imageFile);
    if (!uploadResult?.image_link) {
      setFeedback({
        type: "error",
        message: "Failed to upload blog image",
      });
      setSaving(false);
      return;
    }
    uploadedImageUrl = uploadResult.image_link;
  }

  const normalized: Blog = {
  ...blog,
  image: uploadedImageUrl,
  imageFile: undefined,
  category: blog.featured ? "19" : "20",
  createdBy: blog.createdBy ?? userId,
  status: "published",
  readTime: blog.readTime || "5 min read",
  date: blog.date || new Date().toLocaleDateString(),
};


  let success = false;
  let apiMessage = "";

  if (editingBlog) {
    const result = await blogRepo.updateBlogV2(normalized);
    success = result.success;
    apiMessage = result.message;

    if (success) {
      setBlogs(prev =>
        prev.map(b =>
          b.id === blog.id
            ? { ...b, ...normalized }
            : b
        )
      );
      setFeedback({
        type: "success",
        message: "Blog updated successfully",
      });
    }
  } else {
    const result = await blogRepo.createBlogV2(normalized);
    success = result.success;
    apiMessage = result.message;

    if (success) {
      const { blogs: apiBlogs } = await fetchBlogsForCategories(1, PAGE_SIZE);
      setBlogs(apiBlogs.map(mapApiBlogToBlog));
      setPage(2);
      setHasMore(true);

      setFeedback({
        type: "success",
        message: "Blog created successfully",
      });
    }
  }

  if (!success) {
    setFeedback({
      type: "error",
      message: apiMessage || "Failed to save blog",
    });
  }

  setEditingBlog(null);
  setIsAddOpen(false);
  setSaving(false);
};


  // ----------------------------------
  // UI
  // ----------------------------------
  return (
    <div className="max-w-7xl mx-auto px-6 py-10 text-foreground">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold">Blogs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage all blog content.
          </p>
        </div>

        <Button onClick={() => setIsAddOpen(true)} disabled={saving}>
          Add Blog
        </Button>
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground">
          Loading blogs...
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogs.map(blog => (
            <div
              key={blog.id}
              className="bg-background border rounded-xl overflow-hidden hover:shadow-lg transition flex flex-col"
            >
              <img
                src={blog.image}
                alt={blog.title}
                className="h-40 w-full object-cover"
              />

              <div className="p-4 flex flex-col flex-1">
                <h3 className="font-semibold line-clamp-2">
                  {blog.title}
                </h3>

                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                  {blog.excerpt}
                </p>

                <div className="mt-4 flex justify-end gap-2">
                  <button
                    onClick={() => handleEditBlog(blog)}
                    className="p-2 rounded-md border hover:bg-muted"
                    disabled={saving}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>

                  <button
                    onClick={() => setBlogToDelete(blog)}
                    className="p-2 rounded-md border hover:bg-red-500/10"
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {loadingMore && (
        <div className="text-center text-muted-foreground mt-6">
          Loading more blogs...
        </div>
      )}

      {!hasMore && (
        <div className="text-center text-muted-foreground mt-6 text-sm">
          No more blogs to load
        </div>
      )}

      {/* DELETE CONFIRM DIALOG */}
      {blogToDelete && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-background rounded-2xl border shadow-xl p-6">
            <h3 className="text-lg font-semibold mb-3">
              Delete blog
            </h3>

            <p className="text-sm text-muted-foreground mb-6">
              Are you sure you want to delete{" "}
              <span className="font-medium text-foreground">
                {blogToDelete.title}
              </span>
              ? This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setBlogToDelete(null)}
                disabled={saving}
              >
                Cancel
              </Button>

              <Button
                variant="destructive"
                onClick={confirmDelete}
                disabled={saving}
              >
                {saving ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS / ERROR DIALOG */}
      {feedback && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-background rounded-2xl border shadow-xl p-6 text-center">
            <div
              className={`mx-auto mb-4 h-12 w-12 rounded-full flex items-center justify-center ${
                feedback.type === "success"
                  ? "bg-green-500/10 text-green-600"
                  : "bg-red-500/10 text-red-600"
              }`}
            >
              {feedback.type === "success" ? "✓" : "!"}
            </div>

            <h3 className="text-lg font-semibold mb-2">
              {feedback.type === "success" ? "Success" : "Error"}
            </h3>

            <p className="text-sm text-muted-foreground mb-6">
              {feedback.message}
            </p>

            <Button onClick={() => setFeedback(null)} className="w-full">
              OK
            </Button>
          </div>
        </div>
      )}

      {/* ADD / EDIT MODAL */}
      {isAddOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => {
            setIsAddOpen(false);
            setEditingBlog(null);
          }}
        >
          <div
            className="w-full max-w-xl max-h-[90vh] bg-background rounded-2xl border shadow-xl flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-xl font-semibold">
                {editingBlog ? "Edit Blog" : "Add New Blog"}
              </h3>

              <button
                onClick={() => {
                  setIsAddOpen(false);
                  setEditingBlog(null);
                }}
                className="text-muted-foreground hover:text-foreground text-xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              <AddBlogForm
                key={editingBlog?.id ?? "new"}
                initialBlog={editingBlog}
                loading={saving}
                onCancel={() => {
                  setEditingBlog(null);
                  setIsAddOpen(false);
                }}
                onSubmit={handleSubmit}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
